import { useEffect, useRef } from "react";

/**
 * Minimum gap between attention-driven presence touches. Keeps React work bounded while still
 * marking activity well before the five-minute active window expires.
 */
export const HUB_PRESENCE_ATTENTION_TOUCH_MIN_INTERVAL_MS = 2_000;

/**
 * Pointer, keys, scroll, wheel, and focus movement anywhere in the window count as presence
 * activity. Events are throttled so we do not spam React work. The presence provider publishes
 * only state transitions, route/visibility changes, and the first activity after becoming idle.
 */
export function useHubPresenceAttention(onAttention: () => void, enabled: boolean): void {
  const onRef = useRef(onAttention);
  onRef.current = onAttention;

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }

    let lastFire = 0;

    const maybeFire = (): void => {
      const now = Date.now();
      if (now - lastFire < HUB_PRESENCE_ATTENTION_TOUCH_MIN_INTERVAL_MS) {
        return;
      }
      lastFire = now;
      onRef.current();
    };

    const passiveCapture = { capture: true, passive: true } as const;
    const scrollCapture = { capture: true, passive: true } as const;
    const keyCapture = { capture: true, passive: true } as const;

    window.addEventListener("pointerdown", maybeFire, passiveCapture);
    window.addEventListener("pointermove", maybeFire, passiveCapture);
    window.addEventListener("touchstart", maybeFire, passiveCapture);
    window.addEventListener("touchmove", maybeFire, passiveCapture);
    window.addEventListener("wheel", maybeFire, passiveCapture);
    window.addEventListener("scroll", maybeFire, scrollCapture);
    window.addEventListener("keydown", maybeFire, keyCapture);
    window.addEventListener("focusin", maybeFire, passiveCapture);

    return () => {
      window.removeEventListener("pointerdown", maybeFire, passiveCapture);
      window.removeEventListener("pointermove", maybeFire, passiveCapture);
      window.removeEventListener("touchstart", maybeFire, passiveCapture);
      window.removeEventListener("touchmove", maybeFire, passiveCapture);
      window.removeEventListener("wheel", maybeFire, passiveCapture);
      window.removeEventListener("scroll", maybeFire, scrollCapture);
      window.removeEventListener("keydown", maybeFire, keyCapture);
      window.removeEventListener("focusin", maybeFire, passiveCapture);
    };
  }, [enabled]);
}
